import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'openblock-vm';

import analytics from '../lib/analytics';

import {compose} from 'redux';
import {connect} from 'react-redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import {showAlertWithTimeout, showStandardAlert} from '../reducers/alerts';
import {openTierModal} from '../reducers/modals';
import {requestNewProject} from '../reducers/project-state';
import extensionLibraryContent from '../lib/libraries/extensions/index.jsx';

import LibraryComponent, {ALL_TAG} from '../components/library/library.jsx';
import extensionIcon from '../components/action-menu/icon--sprite.svg';

const messages = defineMessages({
    extensionTitle: {
        defaultMessage: 'Choose an Extension',
        description: 'Heading for the extension library',
        id: 'gui.extensionLibrary.chooseAnExtension'
    },
    extensionUrl: {
        defaultMessage: 'Enter the URL of the extension',
        description: 'Prompt for unoffical extension url',
        id: 'gui.extensionLibrary.extensionUrl'
    },
    shieldTag: {
        id: 'gui.library.shieldTag',
        defaultMessage: 'Shield',
        description: 'Shield tag to filter all shield libraries.'
    },
    actuatorTag: {
        id: 'gui.library.actuatorTag',
        defaultMessage: 'Actuator',
        description: 'Actuator tag to filter all actuator libraries.'
    },
    sensorTag: {
        id: 'gui.library.sensorTag',
        defaultMessage: 'Sensor',
        description: 'Sensor tag to filter all sensor libraries.'
    },
    displayTag: {
        id: 'gui.library.displayTag',
        defaultMessage: 'Display',
        description: 'Display tag to filter all display libraries.'
    },
    communicationTag: {
        id: 'gui.library.communicationTag',
        defaultMessage: 'Communication',
        description: 'Communication tag to filter all communication libraries.'
    },
    otherTag: {
        id: 'gui.library.otherTag',
        defaultMessage: 'Other',
        description: 'Other tag to filter all other libraries.'
    },
    aiTag: {
        id: 'gui.library.aiTag',
        defaultMessage: 'Artificial Intelligence',
        description: 'AI tag to filter all AI libraries.'
    },
    tifBotTag: {
        id: 'gui.library.tifBotTag',
        defaultMessage: 'TIF BOT',
        description: 'TIF BOT tag to filter all TIF BOT extensions.'
    },
    droneTag: {
        id: 'gui.library.droneTag',
        defaultMessage: 'Drone',
        description: 'Drone tag to filter all Lite Wing Drone extensions.'
    },
    dogTag: {
        id: 'gui.library.dogTag',
        defaultMessage: 'Robotic Dog',
        description: 'Robotic Dog tag to filter all Robotic Dog extensions.'
    },
    armTag: {
        id: 'gui.library.armTag',
        defaultMessage: 'Robotic Arm',
        description: 'Robotic Arm tag to filter all Robotic Arm extensions.'
    }
});

const SHIELD_TAG = {tag: 'shield', intlLabel: messages.shieldTag};
const ACTUATOR_TAG = {tag: 'actuator', intlLabel: messages.actuatorTag};
const SENSOR_TAG = {tag: 'sensor', intlLabel: messages.sensorTag};
const DISPLAY_TAG = {tag: 'display', intlLabel: messages.displayTag};
const COMMUNICATION_TAG = {tag: 'communication', intlLabel: messages.communicationTag};
const OTHER_TAG = {tag: 'other', intlLabel: messages.otherTag};
const AI_TAG = {tag: 'AI', intlLabel: messages.aiTag};
const TIF_BOT_TAG = {tag: 'tifbot', intlLabel: messages.tifBotTag};
const DRONE_TAG = {tag: 'drone', intlLabel: messages.droneTag};
const DOG_TAG = {tag: 'dog', intlLabel: messages.dogTag};
const ARM_TAG = {tag: 'arm', intlLabel: messages.armTag};
const tagListPrefix = [AI_TAG, SHIELD_TAG, ACTUATOR_TAG, SENSOR_TAG, DISPLAY_TAG, COMMUNICATION_TAG, OTHER_TAG];

class ExtensionLibrary extends React.PureComponent {
    constructor (props) {
        super(props);
        bindAll(this, [
            'updateScratchExtensions',
            'updateDeviceExtensions',
            'handleItemSelect'
        ]);
        this.state = {
            scratchExtensions: [],
            deviceExtensions: []
        };
    }

    componentDidMount () {
        if (this.props.isRealtimeMode) {
            this.updateScratchExtensions();
        } else {
            this.updateDeviceExtensions();
        }
    }

    updateScratchExtensions () {
        this.props.vm.extensionManager.getExtensionsList(Object.assign([], extensionLibraryContent))
            .then(data => {
                if (data) {
                    this.setState({scratchExtensions: data});
                }
            });
    }

    updateDeviceExtensions () {
        this.props.vm.extensionManager.getDeviceExtensionsList()
            .then(data => {
                if (data) {
                    this.setState({deviceExtensions: data});
                }
            });
    }

    handleItemSelect (item) {
        const id = item.extensionId;
        
        // AI Extension Limit Check for Free Tier
        if (this.props.userTier === 'free' && item.tags && item.tags.includes('AI')) {
            if (!this.props.vm.extensionManager.isExtensionLoaded(id) && 
                !this.props.vm.extensionManager.isDeviceExtensionLoaded(id)) {
                if (this.props.aiUsageCount >= this.props.aiUsageLimit) {
                    this.props.onShowAIUsageLimit();
                    return;
                }

                // Update usage in database
                this.props.onUpdateUsage('ai_usage');
            }
        }

        if (this.props.isRealtimeMode) {
            let url = item.extensionURL ? item.extensionURL : id;
            if (!item.disabled && !id) {
                // eslint-disable-next-line no-alert
                url = prompt(this.props.intl.formatMessage(messages.extensionUrl));
            }
            if (id && !item.disabled) {
                if (this.props.vm.extensionManager.isExtensionLoaded(url)) {
                    this.props.vm.extensionManager.unloadExtension(url);
                    this.updateScratchExtensions();
                    
                    if (id === 'monkeygame') {
                        // Restore default project to clear the monkey game assets
                        this.props.dispatch(requestNewProject(false));
                    }
                } else {
                    this.props.vm.extensionManager.loadExtensionURL(url).then(() => {
                        this.updateScratchExtensions();
                        analytics.event({
                            category: 'extensions',
                            action: 'select extension',
                            label: id
                        });
                        
                        if (id === 'monkeygame') {
                            fetch('static/monkeygame.sb3')
                                .then(response => response.arrayBuffer())
                                .then(buffer => {
                                    this.props.vm.loadProject(new Uint8Array(buffer));
                                })
                                .catch(err => {
                                    console.error('Failed to load monkeygame project:', err);
                                });
                        }
                    });
                }
            }
        } else if (id && !item.disabled) {
            if (this.props.vm.extensionManager.isDeviceExtensionLoaded(id)) {
                this.props.vm.extensionManager.unloadDeviceExtension(id);
                this.updateDeviceExtensions();
            } else {
                this.props.vm.extensionManager.loadDeviceExtension(id).then(() => {
                    this.updateDeviceExtensions();
                    analytics.event({
                        category: 'extensions',
                        action: 'select device extension',
                        label: id
                    });
                })
                    .catch(err => {
                        // TODO add a alet device extension load failed. and change the state to bar to failed state
                        console.error(err); // eslint-disable-line no-console
                    });
            }
        }
    }
    render () {
        let extensionLibraryThumbnailData = [];
        const device = this.props.deviceData.find(dev => dev.deviceId === this.props.deviceId);
        const filterAndSort = extensions => extensions.filter(extension => {
            if (extension.supportDevice) {
                return extension.supportDevice.includes(this.props.deviceId) ||
                extension.supportDevice.includes(device.deviceExtensionsCompatible) ||
                extension.supportDevice.includes('*');
            }
            return true;
        })
            .map(extension => {
                const isAI = extension.tags && extension.tags.includes('AI');
                let isAILocked = false;
                if (isAI && this.props.userTier === 'free') {
                    if (this.props.aiUsageCount >= this.props.aiUsageLimit) {
                        isAILocked = true;
                    }
                }
                return {
                    rawURL: extension.iconURL || extensionIcon,
                    isAILocked: isAILocked,
                    ...extension
                };
            })
            .sort((a, b) => {
                if ((b.isLoaded !== true) && (a.isLoaded === true)) return -1;
                return 1;
            });

        if (this.props.isRealtimeMode) {
            extensionLibraryThumbnailData = filterAndSort(this.state.scratchExtensions);
        } else {
            extensionLibraryThumbnailData = filterAndSort(this.state.deviceExtensions);
        }

        const deviceId = (this.props.deviceId || '').toLowerCase();
        const isTifBot = deviceId.includes('tifbot');
        const isDrone = deviceId.includes('drone');
        const isDog = deviceId.includes('dog');
        const isArm = deviceId.includes('arm');
        const isKitDevice = isTifBot || isDrone || isDog || isArm;
        let tags;
        if (this.props.isRealtimeMode) {
            tags = [AI_TAG];
        } else if (isKitDevice) {
            tags = [];
        } else {
            tags = [SHIELD_TAG, ACTUATOR_TAG, SENSOR_TAG, DISPLAY_TAG, COMMUNICATION_TAG, OTHER_TAG];
        }
        
        let tagListPrefix = [ALL_TAG];
        if (isArm) {
            tagListPrefix = [ARM_TAG, ALL_TAG];
        } else if (isDog) {
            tagListPrefix = [DOG_TAG, ALL_TAG];
        } else if (isDrone) {
            tagListPrefix = [DRONE_TAG, ALL_TAG];
        } else if (isTifBot) {
            tagListPrefix = [TIF_BOT_TAG, ALL_TAG];
        }

        return (
            <LibraryComponent
                autoClose={false}
                data={extensionLibraryThumbnailData}
                defaultTag={isArm ? ARM_TAG.tag : (isDog ? DOG_TAG.tag : (isDrone ? DRONE_TAG.tag : (isTifBot ? TIF_BOT_TAG.tag : ALL_TAG.tag)))}
                filterable
                tagListPrefix={tagListPrefix}
                tags={tags}
                id="extensionLibrary"
                isUnloadble
                title={this.props.intl.formatMessage(messages.extensionTitle)}
                visible={this.props.visible}
                onItemSelected={this.handleItemSelect}
                onRequestClose={this.props.onRequestClose}
            />
        );
    }
}

ExtensionLibrary.propTypes = {
    deviceData: PropTypes.instanceOf(Array).isRequired,
    deviceId: PropTypes.string,
    intl: intlShape.isRequired,
    isRealtimeMode: PropTypes.bool,
    onRequestClose: PropTypes.func,
    visible: PropTypes.bool,
    userTier: PropTypes.string,
    aiUsageCount: PropTypes.number,
    aiUsageLimit: PropTypes.number,
    onUpdateUsage: PropTypes.func,
    dispatch: PropTypes.func,
    vm: PropTypes.instanceOf(VM).isRequired // eslint-disable-line react/no-unused-prop-types
};

const mapStateToProps = state => ({
    deviceData: state.scratchGui.deviceData.deviceData,
    deviceId: state.scratchGui.device.deviceId,
    isRealtimeMode: state.scratchGui.programMode.isRealtimeMode,
    userTier: state.scratchGui.tier.tier,
    aiUsageCount: state.scratchGui.tier ? state.scratchGui.tier.aiUsageCount : 0,
    aiUsageLimit: state.scratchGui.tier ? state.scratchGui.tier.aiUsageLimit : 10
});

const mapDispatchToProps = dispatch => ({
    onShowTierLimits: () => dispatch(openTierModal()),
    onShowAIUsageLimit: () => showAlertWithTimeout(dispatch, 'freeTierAIUsageLimit'),
    onUpdateUsage: type => {
        const { updateUsage } = require('../lib/usage-api');
        const { setUsage } = require('../reducers/tier');
        updateUsage(type).then(usage => {
            if (usage) {
                dispatch(setUsage(usage));
            }
        });
    },
    dispatch: dispatch
});


export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(ExtensionLibrary);
